<template>
  <div class="tc-onb-shell">
    <AppHeader>
      <button
        type="button"
        class="btn btn-outline-secondary btn-sm"
        @click="router.replace('/admin')"
      >Back to admin</button>
    </AppHeader>

    <!-- ── Saved setup ── -->
    <div v-if="mode === 'summary'" class="tc-onb-solo" data-testid="onb-summary">
      <main class="tc-onb-pane">
        <h1 class="tc-onb-title">Your setup</h1>
        <p class="tc-onb-lede">What TimeCapture uses to fill your timesheet.</p>

        <div v-if="reviewCards.length === 0" class="tc-onb-flag">
          No rules saved — the name-matching floor is always on.
        </div>

        <div class="tc-onb-review-list">
          <div
            v-for="card in reviewCards"
            :key="card.rule.id"
            class="tc-onb-review-item"
            :data-testid="`onb-summary-${card.rule.id}`"
          >
            <div class="tc-onb-review-body">
              <div class="tc-onb-review-title">{{ card.rule.name }}</div>
              <div class="tc-onb-review-sub">{{ hitsLabel(card) }}</div>
            </div>
            <button
              type="button"
              class="tc-onb-switch"
              :class="{ 'tc-onb-switch--off': !card.enabled }"
              :aria-label="card.enabled ? 'Enabled' : 'Disabled'"
              :data-testid="`onb-toggle-${card.rule.id}`"
              @click="card.enabled = !card.enabled"
            ></button>
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm"
              :data-testid="`onb-remove-${card.rule.id}`"
              @click="skipCard(card)"
            >Remove</button>
          </div>
          <div v-if="agentGuidance.trim()" class="tc-onb-review-item" data-testid="onb-summary-guidance">
            <div class="tc-onb-review-body">
              <div class="tc-onb-review-title">AI guidance</div>
              <div class="tc-onb-review-sub">{{ guidanceSummary }}</div>
            </div>
          </div>
        </div>

        <div v-if="saveError" class="alert alert-danger mt-3" role="alert">{{ saveError }}</div>
        <div v-if="saved" class="alert alert-success mt-3" role="status" data-testid="onb-saved">
          Setup saved.
        </div>

        <div class="tc-onb-footnav">
          <button
            type="button"
            class="btn btn-outline-secondary"
            data-testid="onb-rerun"
            @click="startWizard"
          >Run setup again</button>
          <span class="tc-onb-footnav-spacer"></span>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="saving"
            data-testid="onb-summary-save"
            @click="finish"
          >
            <span v-if="saving" class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>
            Save changes
          </button>
        </div>
      </main>
    </div>

    <div v-else class="tc-onb-layout">
      <!-- Step rail -->
      <nav class="tc-onb-rail" aria-label="Setup steps">
        <div class="tc-onb-rail-label">Setup</div>
        <div class="tc-onb-rail-steps">
          <button
            v-for="(step, i) in steps"
            :key="step.key"
            type="button"
            class="tc-onb-step-item"
            :class="{ 'tc-onb-step-item--active': i === current, 'tc-onb-step-item--done': i < current }"
            :data-testid="`onb-step-${step.key}`"
            @click="goTo(i)"
          >
            <span class="tc-onb-step-num">{{ i < current ? '✓' : i + 1 }}</span>
            <span class="tc-onb-step-label">{{ step.label }}</span>
          </button>
        </div>
        <div class="tc-onb-rail-privacy">
          <strong>Your data stays yours.</strong>
          We check samples, then discard them. We keep only the rules you approve.
        </div>
      </nav>

      <!-- Pane -->
      <main class="tc-onb-pane">
        <p class="tc-onb-eyebrow">Step {{ current + 1 }} of {{ steps.length }}</p>

        <!-- ── Scan ── -->
        <section v-if="stepKey === 'scan'" data-testid="onb-pane-scan">
          <h1 class="tc-onb-title">We looked at your Vantagepoint</h1>
          <p class="tc-onb-lede">How well can your projects be matched automatically?</p>

          <div v-if="scanError" class="alert alert-danger" role="alert" data-testid="onb-scan-error">
            {{ scanError }}
            <button type="button" class="btn btn-outline-secondary btn-sm ms-2" @click="runScan">Retry</button>
          </div>

          <div v-else-if="!scan" class="tc-onb-process" data-testid="onb-scan-progress">
            <div class="tc-onb-stage tc-onb-stage--running">
              <span class="tc-onb-stage-dot"></span>
              <span>Checking Vantagepoint and your recent activity</span>
              <span class="tc-onb-chip">code</span>
            </div>
          </div>

          <template v-else>
            <div class="tc-onb-evidence" data-testid="onb-scan-result">
              <div class="tc-onb-evidence-label">Your project catalogue</div>
              <div class="tc-onb-stat-row">
                <div class="tc-onb-stat">
                  <div class="tc-onb-stat-val">{{ scan.catalogue.projectCount }}</div>
                  <div class="tc-onb-stat-lbl">projects you can charge to</div>
                </div>
                <div class="tc-onb-stat tc-onb-stat--good">
                  <div class="tc-onb-stat-val">{{ pct(scan.catalogue.withClientName, scan.catalogue.projectCount) }}%</div>
                  <div class="tc-onb-stat-lbl">have a client name attached</div>
                </div>
                <div class="tc-onb-stat tc-onb-stat--good">
                  <div class="tc-onb-stat-val">{{ pct(scan.baseline.settled, scan.baseline.sampled) }}%</div>
                  <div class="tc-onb-stat-lbl">of recent events match by name alone</div>
                </div>
                <div class="tc-onb-stat tc-onb-stat--good">
                  <div class="tc-onb-stat-val">{{ pct(scan.baseline.shortlistLe3, scan.baseline.sampled) }}%</div>
                  <div class="tc-onb-stat-lbl">come down to 3 candidates or fewer</div>
                </div>
              </div>
            </div>

            <div class="tc-onb-evidence" data-testid="onb-scan-sources">
              <div class="tc-onb-evidence-label">What we can read</div>
              <ul v-if="scannedSources.length" class="tc-onb-source-list">
                <li
                  v-for="s in scannedSources"
                  :key="s.id"
                  class="tc-onb-source"
                  :data-testid="`onb-scan-source-${s.id}`"
                >
                  <SourceBadge :source="s.id" variant="labeled" />
                  <span class="tc-onb-source-facts">{{ s.facts.join(' · ') }}</span>
                </li>
              </ul>
              <p v-else class="tc-onb-evidence-text">No sources connected yet.</p>
            </div>

            <p class="tc-onb-lede">A few questions about how your firm works will cover the rest.</p>
          </template>
        </section>

        <!-- ── Freshdesk / Calendar / Drain (question steps) ── -->
        <section v-else-if="isQuestionStep" :data-testid="`onb-pane-${stepKey}`">
          <h1 class="tc-onb-title">{{ question.title }}</h1>

          <div v-if="question.evidence" class="tc-onb-evidence">
            <div class="tc-onb-evidence-label">What we noticed</div>
            <p class="tc-onb-evidence-text">{{ question.evidence }}</p>
          </div>

          <div class="tc-onb-q">
            <p class="tc-onb-q-text">{{ question.question }}</p>
            <textarea
              v-model="answers[stepKey]"
              class="form-control tc-onb-answer"
              rows="3"
              :data-testid="`onb-answer-${stepKey}`"
            ></textarea>
            <button
              v-if="question.clarify"
              type="button"
              class="tc-onb-clarify-link"
              @click="clarifyOpen[stepKey] = !clarifyOpen[stepKey]"
            >Not sure what we mean?</button>
            <div v-if="question.clarify && clarifyOpen[stepKey]" class="tc-onb-clarify">
              <div class="tc-onb-clarify-q">{{ question.clarify.q }}</div>
              <div class="tc-onb-clarify-a">{{ question.clarify.a }}</div>
            </div>
            <div class="mt-3">
              <button
                type="button"
                class="btn btn-primary"
                :disabled="flowFor(stepKey).running || !answers[stepKey].trim()"
                :data-testid="`onb-check-${stepKey}`"
                @click="checkAnswer(stepKey)"
              >Check my answer</button>
            </div>
          </div>

          <div v-if="flowFor(stepKey).running || flowFor(stepKey).stage >= 0" class="tc-onb-process">
            <div
              v-for="(stage, i) in compileStages(stepKey)"
              :key="i"
              class="tc-onb-stage"
              :class="{
                'tc-onb-stage--running': flowFor(stepKey).running && flowFor(stepKey).stage === i,
                'tc-onb-stage--done': flowFor(stepKey).stage > i,
              }"
            >
              <span class="tc-onb-stage-dot"></span>
              <span>{{ stage.text }}</span>
              <span class="tc-onb-chip">{{ stage.chip }}</span>
            </div>
          </div>

          <div v-if="flowFor(stepKey).error" class="alert alert-danger mt-3" role="alert">
            {{ flowFor(stepKey).error }}
          </div>

          <div
            v-if="flagNotes[stepKey]"
            class="tc-onb-flag"
            :data-testid="`onb-flag-${stepKey}`"
          >
            <strong>Nothing to set up here.</strong>
            We've flagged this for your onboarding contact.
            <p class="tc-onb-flag-why">{{ flagNotes[stepKey] }}</p>
          </div>

          <RuleCards
            :cards="cardsFor(stepKey)"
            @enable="enableCard"
            @skip="skipCard"
            @edited="revalidateCard"
          />
        </section>

        <!-- ── Phases & internal time ── -->
        <section v-else-if="stepKey === 'phases'" data-testid="onb-pane-phases">
          <h1 class="tc-onb-title">Phases, and time that isn't client work</h1>

          <template v-if="orgJoinCard">
            <div class="tc-onb-evidence">
              <div class="tc-onb-evidence-label">What we noticed</div>
              <p class="tc-onb-evidence-text">
                Your projects span {{ scan?.catalogue.distinctOrgs }} practices. When a project has
                phases per practice, the person's own practice picks the phase.
              </p>
            </div>
            <RuleCards
              :cards="[orgJoinCard]"
              @enable="enableCard"
              @skip="skipCard"
              @edited="revalidateCard"
            />
          </template>

          <div class="tc-onb-q mt-4">
            <p class="tc-onb-q-text">{{ QUESTIONS.phases.question }}</p>
            <textarea
              v-model="answers.phases"
              class="form-control tc-onb-answer"
              rows="3"
              data-testid="onb-answer-phases"
            ></textarea>
            <div class="mt-3">
              <button
                type="button"
                class="btn btn-primary"
                :disabled="flowFor('phases').running || !answers.phases.trim()"
                data-testid="onb-check-phases"
                @click="checkAnswer('phases')"
              >Check my answer</button>
            </div>
          </div>

          <div v-if="flowFor('phases').running || flowFor('phases').stage >= 0" class="tc-onb-process">
            <div
              v-for="(stage, i) in compileStages('phases')"
              :key="i"
              class="tc-onb-stage"
              :class="{
                'tc-onb-stage--running': flowFor('phases').running && flowFor('phases').stage === i,
                'tc-onb-stage--done': flowFor('phases').stage > i,
              }"
            >
              <span class="tc-onb-stage-dot"></span>
              <span>{{ stage.text }}</span>
              <span class="tc-onb-chip">{{ stage.chip }}</span>
            </div>
          </div>

          <div v-if="flowFor('phases').error" class="alert alert-danger mt-3" role="alert">
            {{ flowFor('phases').error }}
          </div>
          <div v-if="flagNotes.phases" class="tc-onb-flag" data-testid="onb-flag-phases">
            <strong>Nothing to set up here.</strong>
            We've flagged this for your onboarding contact.
            <p class="tc-onb-flag-why">{{ flagNotes.phases }}</p>
          </div>

          <RuleCards
            :cards="cardsFor('phases')"
            @enable="enableCard"
            @skip="skipCard"
            @edited="revalidateCard"
          />
        </section>

        <!-- ── AI guidance ── -->
        <section v-else-if="stepKey === 'guidance'" data-testid="onb-pane-guidance">
          <h1 class="tc-onb-title">Teach the AI your conventions</h1>
          <p class="tc-onb-lede">
            Plain English the AI reads on every draft — quirks, exceptions, and anything a rule
            can't capture. "Standups Jane runs are internal, not billable." "Anything tagged ACME
            is the Acme redesign."
          </p>

          <div class="tc-onb-q">
            <textarea
              v-model="agentGuidance"
              class="form-control tc-onb-answer"
              rows="6"
              :maxlength="AGENT_CONTEXT_MAX"
              data-testid="onb-guidance"
            ></textarea>
            <div class="d-flex align-items-center justify-content-between gap-2 mt-2">
              <small class="text-muted">{{ agentGuidance.length }} / {{ AGENT_CONTEXT_MAX }}</small>
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm"
                :disabled="previewing || !agentGuidance.trim()"
                data-testid="onb-guidance-preview"
                @click="runPreview"
              >
                <span v-if="previewing" class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>
                Preview accuracy
              </button>
            </div>
          </div>

          <div v-if="previewError" class="alert alert-danger mt-3" role="alert">{{ previewError }}</div>

          <div v-else-if="preview" class="tc-onb-estimate mt-3" data-testid="onb-guidance-result">
            <template v-if="preview.ambiguous === 0">
              <p>Every sampled event already settles on its own — nothing ambiguous to test right now.</p>
            </template>
            <template v-else>
              <div class="tc-onb-estimate-big">{{ preview.placedWith }} / {{ preview.ambiguous }}</div>
              <p>
                unclear events the AI now places with confidence — up from
                {{ preview.placedWithout }} without your guidance. The AI is non-deterministic,
                so treat this as indicative.
              </p>
            </template>
          </div>
        </section>

        <!-- ── Review ── -->
        <section v-else-if="stepKey === 'review'" data-testid="onb-pane-review">
          <h1 class="tc-onb-title">Review &amp; finish</h1>
          <p class="tc-onb-lede">Change any of it later by re-running setup.</p>

          <div v-if="estimate && estimate.sampled > 0" class="tc-onb-estimate" data-testid="onb-estimate">
            <div class="tc-onb-estimate-big">~{{ pct(estimate.settled, estimate.sampled) }}%</div>
            <p>of your sampled events would fill themselves in. The rest ask for a quick answer —
              and every answer teaches TimeCapture.</p>
          </div>

          <div v-if="reviewCards.length === 0" class="tc-onb-flag">
            No rules yet — the name-matching floor is always on. You can finish setup with just a
            comment format, or go back and answer a question.
          </div>

          <div class="tc-onb-review-list">
            <div
              v-for="card in reviewCards"
              :key="card.rule.id"
              class="tc-onb-review-item"
              :data-testid="`onb-review-${card.rule.id}`"
            >
              <div class="tc-onb-review-body">
                <div class="tc-onb-review-title">{{ card.rule.name }}</div>
                <div class="tc-onb-review-sub">{{ hitsLabel(card) }}</div>
              </div>
              <button
                type="button"
                class="tc-onb-switch"
                :class="{ 'tc-onb-switch--off': !card.enabled }"
                :aria-label="card.enabled ? 'Enabled' : 'Disabled'"
                :data-testid="`onb-toggle-${card.rule.id}`"
                @click="card.enabled = !card.enabled"
              ></button>
            </div>
            <div v-if="agentGuidance.trim()" class="tc-onb-review-item" data-testid="onb-review-guidance">
              <div class="tc-onb-review-body">
                <div class="tc-onb-review-title">AI guidance</div>
                <div class="tc-onb-review-sub">{{ guidanceSummary }}</div>
              </div>
            </div>
          </div>

          <div class="tc-onb-privacy-recap">
            <strong>What we kept:</strong> the rules above and their match numbers — nothing else.
            The samples and your typed answers are already gone. Who can see which projects is
            always Vantagepoint's call.
          </div>

          <div v-if="saveError" class="alert alert-danger mt-3" role="alert">{{ saveError }}</div>
          <div v-if="saved" class="alert alert-success mt-3" role="status" data-testid="onb-saved">
            Setup saved.
          </div>
        </section>

        <!-- Footer nav -->
        <div class="tc-onb-footnav">
          <button
            v-if="current > 0"
            type="button"
            class="btn btn-outline-secondary"
            @click="goTo(current - 1)"
          >Back</button>
          <span class="tc-onb-footnav-spacer"></span>
          <button
            v-if="stepKey !== 'review'"
            type="button"
            class="btn btn-primary"
            :disabled="stepKey === 'scan' && !scan"
            data-testid="onb-continue"
            @click="goTo(current + 1)"
          >Continue</button>
          <button
            v-else
            type="button"
            class="btn btn-primary"
            :disabled="saving"
            data-testid="onb-finish"
            @click="finish"
          >
            <span v-if="saving" class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>
            Finish setup
          </button>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import AppHeader from '../components/AppHeader.vue';
import RuleCards, { type RuleCard } from '../components/OnboardingRuleCards.vue';
import { zeroHitReason } from '../components/onboardingRuleLabel';
import SourceBadge from '../components/ui/SourceBadge.vue';
import {
  compileAnswer,
  getOnboardingRules,
  getOnboardingScan,
  previewAgentContext,
  saveOnboardingRules,
  validateRules,
  type PreviewContextResponse,
  type ScanResponse,
  type SettleScan,
  type SourceSampleScan,
} from '../api/onboarding';
import { ApiClientError } from '../api/client';
import { useTimezoneStore } from '../stores/timezone';
import type { SourceId } from '../api/calendar';

const router = useRouter();
const tzStore = useTimezoneStore();

type QuestionKey = 'freshdesk' | 'calendar' | 'phases';

interface Question {
  key: string;               // BE questionKey
  source: string | null;     // sample scope for validation
  title: string;
  question: string;
  evidence?: string;
  clarify?: { q: string; a: string };
}

const QUESTIONS: Record<QuestionKey, Question> = {
  freshdesk: {
    key: 'freshdesk_link',
    source: 'freshdesk',
    title: 'How do tickets connect to projects?',
    question: 'How does a ticket get connected to a project today?',
    clarify: {
      q: 'What are we asking for?',
      a: 'Anything that links a ticket to billable work — a tag someone adds, the company the '
        + 'ticket sits under, or a habit like putting the project number in the subject. '
        + 'No habit at all is a fine answer too.',
    },
  },
  calendar: {
    key: 'calendar_refs',
    source: 'outlook',
    title: "What's in your meeting titles?",
    question: 'Do your people put project references in meeting titles?',
    clarify: {
      q: 'What counts as a project reference?',
      a: 'Anything that identifies the Vantagepoint project — the project number, a client PO '
        + 'number, or a code your firm uses in a consistent format.',
    },
  },
  phases: {
    key: 'internal_time',
    source: 'outlook',
    title: '',
    question: "Where does non-client time go — leave, holidays, internal meetings?",
  },
};

// ── Wizard state ────────────────────────────────────────────────────────────

const scan = ref<ScanResponse | null>(null);
const scanError = ref<string | null>(null);

const answers = reactive<Record<string, string>>({
  freshdesk: '', calendar: '', phases: '',
});
const clarifyOpen = reactive<Record<string, boolean>>({});
const flagNotes = reactive<Record<string, string | null>>({
  freshdesk: null, calendar: null, phases: null,
});

interface CompileFlow { stage: number; running: boolean; error: string | null }
const flows = reactive<Record<string, CompileFlow>>({
  freshdesk: { stage: -1, running: false, error: null },
  calendar: { stage: -1, running: false, error: null },
  phases: { stage: -1, running: false, error: null },
});

const cards = ref<RuleCard[]>([]);
// No longer authored here — the format was never applied to the VP comment.
// Loaded and saved back untouched so a tenant's stored value survives until
// the follow-up decides whether to honour it or drop it.
const commentTemplate = ref('');
const storedFlaggedNotes = ref<string[]>([]);

// Free-text guidance the agent reads on every draft (ADR-0045 §4). Kept in sync
// with the BE length bound so the textarea can't submit an over-long value.
const AGENT_CONTEXT_MAX = 4000;
const agentGuidance = ref('');
const preview = ref<PreviewContextResponse | null>(null);
const previewing = ref(false);
const previewError = ref<string | null>(null);

const estimate = ref<SettleScan | null>(null);
const saving = ref(false);
const saved = ref(false);
const saveError = ref<string | null>(null);

// Admins land on their saved setup; the wizard is for a first run or a redo.
const mode = ref<'wizard' | 'summary'>('wizard');

function startWizard(): void {
  mode.value = 'wizard';
  current.value = 0;
  saved.value = false;
  if (!scan.value) void runScan();
}

// ── Steps (dynamic on connected sources) ────────────────────────────────────

const current = ref(0);

const steps = computed(() => {
  const list = [{ key: 'scan', label: 'What we found' }];
  if (sourceConnected('freshdesk')) list.push({ key: 'freshdesk', label: 'Freshdesk' });
  if (sourceConnected('outlook')) list.push({ key: 'calendar', label: 'Calendar' });
  list.push(
    { key: 'phases', label: 'Phases & internal time' },
    { key: 'guidance', label: 'AI guidance' },
    { key: 'review', label: 'Review & finish' },
  );
  return list;
});

const stepKey = computed(() => steps.value[current.value]?.key ?? 'scan');
const isQuestionStep = computed(() => stepKey.value === 'freshdesk' || stepKey.value === 'calendar');

function sourceConnected(id: string): boolean {
  return scan.value?.sources.some(s => s.id === id && s.status === 'connected') ?? false;
}

// What each connected source actually offers the matcher — the scan already
// samples this; the wizard opens by saying so rather than only quoting VP.
const scannedSources = computed(() =>
  (scan.value?.sources ?? [])
    .filter(s => s.status === 'connected')
    .map(s => ({ id: s.id as SourceId, facts: sampleFacts(s.sample) })),
);

function sampleFacts(sample: SourceSampleScan | null): string[] {
  if (!sample || sample.eventCount === 0) return ['Nothing recent to sample'];
  const facts = [`${sample.eventCount} recent events`];
  if (sample.withTags > 0) {
    facts.push(`${sample.tagsResolved} of ${sample.withTags} tags match a project`);
  }
  if (sample.subjectsWithProjectCode > 0) {
    facts.push(`${sample.subjectsWithProjectCode} name a project in the subject`);
  }
  return facts;
}

function goTo(i: number): void {
  if (i < 0 || i >= steps.value.length) return;
  current.value = i;
}

// ── Derived copy ────────────────────────────────────────────────────────────

const question = computed<Question>(() => {
  const q = QUESTIONS[stepKey.value as QuestionKey];
  if (stepKey.value === 'freshdesk') {
    const s = scan.value?.sources.find(x => x.id === 'freshdesk')?.sample;
    return {
      ...q,
      evidence: s && s.eventCount > 0
        ? `We checked your last ${s.eventCount} tickets. ${s.withTags} have tags — and `
          + `${s.tagsResolved} of those tags are Vantagepoint project numbers.`
        : undefined,
    };
  }
  if (stepKey.value === 'calendar') {
    const s = scan.value?.sources.find(x => x.id === 'outlook')?.sample;
    return {
      ...q,
      evidence: s && s.eventCount > 0
        ? `${s.subjectsWithProjectCode} of your last ${s.eventCount} meetings have a project code `
          + 'in the title. Client names are matched automatically — no rule needed for those.'
        : undefined,
    };
  }
  return q;
});

const orgJoinCard = computed(() =>
  cards.value.find(c => c.rule.kind === 'attribute_join') ?? null);

const reviewCards = computed(() =>
  cards.value.filter(c => c.enabled || c.origin === 'existing'));

const guidanceSummary = computed(() => {
  const text = agentGuidance.value.trim();
  return text.length > 90 ? `${text.slice(0, 90)}…` : text;
});

function cardsFor(origin: string): RuleCard[] {
  return cards.value.filter(c => c.origin === origin);
}

function flowFor(key: string): CompileFlow {
  return flows[key];
}

function compileStages(key: string): { text: string; chip: string }[] {
  const testText = key === 'freshdesk'
    ? 'Testing it against your recent tickets'
    : key === 'phases'
      ? 'Checking those codes exist in Vantagepoint'
      : 'Testing it against your sampled events';
  return [
    { text: 'Reading your answer', chip: 'AI' },
    { text: 'Drafting a rule', chip: 'AI' },
    { text: testText, chip: 'code' },
  ];
}

function pct(n: number, of: number): number {
  return of === 0 ? 0 : Math.round((n / of) * 100);
}

function hitsLabel(card: RuleCard): string {
  if (card.unresolvedCodes.length > 0) return `Code not found: ${card.unresolvedCodes.join(', ')}`;
  if (card.rule.kind === 'attribute_join') return 'Live against Vantagepoint';
  if (card.sampleSize === null || card.sampleSize === 0) return 'Not yet tested';
  // Only explain a zero when this run measured the field; a rehydrated rule
  // carries its old score but no observation to reason from.
  if (card.hits === 0 && card.fieldPresent !== null) {
    return zeroHitReason(card.rule.field, card.sampleSize, card.fieldPresent);
  }
  return `Worked on ${card.hits} of ${card.sampleSize}`;
}

// ── Actions ─────────────────────────────────────────────────────────────────

async function runScan(): Promise<void> {
  scanError.value = null;
  scan.value = null;
  try {
    scan.value = await getOnboardingScan(tzStore.selectedZone);
    seedOrgJoinCard();
  } catch (e) {
    scanError.value = e instanceof ApiClientError ? e.message : 'Scan failed. Try again.';
  }
}

function seedOrgJoinCard(): void {
  const suggested = scan.value?.suggestedOrgJoin;
  if (!suggested || cards.value.some(c => c.rule.kind === 'attribute_join')) return;
  cards.value.push({
    rule: suggested,
    hits: null,
    sampleSize: null,
    fieldPresent: null,
    unresolvedCodes: [],
    enabled: false,
    origin: 'phases-orgjoin',
    description: 'Someone in a practice gets that practice\'s phase. If nothing lines up, we ask — a phase is never guessed.',
  });
}

async function checkAnswer(key: string): Promise<void> {
  const q = QUESTIONS[key as QuestionKey];
  const answer = answers[key].trim();
  if (!answer) return;

  const flow = flows[key];
  flow.running = true;
  flow.stage = 0;
  flow.error = null;
  flagNotes[key] = null;
  cards.value = cards.value.filter(c => c.origin !== key);

  const advance = setTimeout(() => { if (flow.stage === 0) flow.stage = 1; }, 700);
  try {
    const compiled = await compileAnswer(q.key, answer);
    clearTimeout(advance);

    if (!compiled.mechanizable) {
      flagNotes[key] = compiled.flagNote ?? "Couldn't turn this into a rule.";
      flow.stage = 2;
      return;
    }

    flow.stage = 2;
    const validation = await validateRules(compiled.rules, q.source, tzStore.selectedZone);
    const statsById = new Map(validation.results.map(r => [r.ruleId, r]));
    cards.value.push(...compiled.rules.map(rule => {
      const stats = statsById.get(rule.id);
      return {
        rule,
        hits: stats?.hits ?? null,
        sampleSize: stats?.sampleSize ?? null,
        fieldPresent: stats?.fieldPresent ?? null,
        unresolvedCodes: stats?.unresolvedCodes ?? [],
        enabled: false,
        origin: key,
      };
    }));
    flow.stage = 3;
  } catch (e) {
    clearTimeout(advance);
    flow.error = e instanceof ApiClientError ? e.message : 'Something went wrong. Try again.';
    flow.stage = -1;
  } finally {
    flow.running = false;
  }
}

function enableCard(card: RuleCard): void {
  card.enabled = true;
}

function skipCard(card: RuleCard): void {
  cards.value = cards.value.filter(c => c !== card);
}

async function revalidateCard(card: RuleCard): Promise<void> {
  if (card.rule.kind === 'attribute_join') return;
  const source = QUESTIONS[card.origin as QuestionKey]?.source ?? null;
  try {
    const validation = await validateRules([card.rule], source, tzStore.selectedZone);
    const stats = validation.results.find(r => r.ruleId === card.rule.id);
    card.hits = stats?.hits ?? null;
    card.sampleSize = stats?.sampleSize ?? null;
    card.fieldPresent = stats?.fieldPresent ?? null;
    card.unresolvedCodes = stats?.unresolvedCodes ?? [];
  } catch {
    card.hits = null;
    card.sampleSize = null;
    card.fieldPresent = null;
  }
}

async function runPreview(): Promise<void> {
  const text = agentGuidance.value.trim();
  if (!text) return;
  previewing.value = true;
  previewError.value = null;
  preview.value = null;
  try {
    preview.value = await previewAgentContext(text, tzStore.selectedZone);
  } catch (e) {
    previewError.value = e instanceof ApiClientError ? e.message : 'Preview failed. Try again.';
  } finally {
    previewing.value = false;
  }
}

async function refreshEstimate(): Promise<void> {
  const rules = reviewCards.value.filter(c => c.enabled).map(c => c.rule);
  try {
    const validation = await validateRules(rules, null, tzStore.selectedZone);
    estimate.value = validation.estimate;
  } catch {
    estimate.value = null;
  }
}

async function finish(): Promise<void> {
  saving.value = true;
  saveError.value = null;
  saved.value = false;
  try {
    const kept = reviewCards.value;
    const notes = [
      ...storedFlaggedNotes.value,
      ...Object.values(flagNotes).filter((n): n is string => !!n),
    ];
    await saveOnboardingRules({
      rules: kept.map(c => ({
        ...c.rule,
        enabled: c.enabled,
        sampleHits: c.hits ?? 0,
        sampleSize: c.sampleSize ?? 0,
      })),
      commentTemplate: commentTemplate.value.trim() || null,
      flaggedNotes: [...new Set(notes)],
      agentContext: agentGuidance.value.trim() || null,
    });
    saved.value = true;
    mode.value = 'summary';
  } catch (e) {
    saveError.value = e instanceof ApiClientError ? e.message : 'Save failed. Try again.';
  } finally {
    saving.value = false;
  }
}

watch(stepKey, (key) => {
  if (key === 'review') void refreshEstimate();
});

// A stale settle-rate is worse than none — drop it the moment the prose changes.
watch(agentGuidance, () => { preview.value = null; });

onMounted(async () => {
  try {
    const existing = await getOnboardingRules();
    commentTemplate.value = existing.commentTemplate ?? '';
    storedFlaggedNotes.value = existing.flaggedNotes;
    agentGuidance.value = existing.agentContext ?? '';
    cards.value.push(...existing.rules.map(rule => ({
      rule,
      hits: rule.sampleHits ?? null,
      sampleSize: rule.sampleSize ?? null,
      fieldPresent: null,
      unresolvedCodes: [],
      enabled: rule.enabled ?? true,
      origin: 'existing',
    })));
    if (existing.rules.length > 0 || existing.commentTemplate || existing.agentContext) {
      mode.value = 'summary';
    }
  } catch {
    // No stored rules is the normal first-run case.
  }
  // The summary needs no sample; scanning waits until setup is actually run.
  if (mode.value === 'wizard') void runScan();
});
</script>
